/**
 * Órdenes — reclamo de no entregadas a Mercado Libre (etapa 2): distintivo y acciones según el
 * expediente vigente de la venta (`nd_claim` de la API unified). Puro: se prueba desde
 * scripts/nd-claim-view.test.ts.
 */
import type { UnifiedOrder } from './types';

export type NdClaim = NonNullable<UnifiedOrder['nd_claim']>;
export type NdClaimTone = 'blue' | 'amber' | 'red' | 'gray' | 'violet';
export type NdClaimAction = 'nd_claim_stop' | 'nd_claim_retry';

/** Clases del distintivo por tono (mismo formato que los distintivos de la fila). */
export const ND_CLAIM_TONE_CLASS: Record<NdClaimTone, string> = {
  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  gray: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300',
  violet: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
};

const d = (iso: string) => {
  const x = new Date(iso);
  return Number.isNaN(x.getTime()) ? '' : `${x.getDate()}/${x.getMonth() + 1}`;
};

/** Honesto: si el dueño ya dijo que no en Decisiones, solo el botón la vuelve a proponer. */
export const ND_SIN_RESPUESTA_TITLE =
  'Para volver a reclamar. Se propone en Decisiones en la revisión diaria (9:30); si ahí ya dijiste que no, se vuelve a proponer en la próxima revisión diaria solo si pulsás Volver a reclamar.';

export function ndClaimChip(
  nd: NdClaim | null | undefined
): { label: string; tone: NdClaimTone; title: string } | null {
  if (!nd) return null;
  const r = nd.round > 1 ? ` · ronda ${nd.round}` : ' · ronda 1';
  const caso = nd.case_id ? `Caso ${nd.case_id}. ` : '';
  switch (nd.status) {
    case 'reclamada':
      return { label: `Reclamada ${d(nd.claimed_at)}${r}`, tone: 'blue', title: `${caso}Esperando respuesta de Mercado Libre.` };
    case 'en_analisis':
      return { label: `En análisis ML${r}`, tone: 'blue', title: `${caso}Mercado Libre la pasó al equipo del país.` };
    case 'respondida':
      return { label: 'Respondida — revisar', tone: 'amber', title: nd.note ?? 'Mercado Libre respondió sin decir si paga.' };
    case 'rechazada':
      return {
        label: `Rechazada: ${(nd.reason ?? '').replace(/^Mercado Libre dice que /, '').split(':')[0].slice(0, 40)}`,
        tone: 'red',
        title: nd.reason ?? '',
      };
    case 'rechazo_canal':
      return { label: 'ML pide reclamar por país', tone: 'amber', title: `${caso}Se propone en Decisiones volver a reclamar explicando la cuenta principal. Si ahí dijiste que no, se vuelve a proponer solo si pulsás Volver a reclamar.` };
    case 'sin_respuesta':
      return { label: 'Volver a reclamar', tone: 'violet', title: ND_SIN_RESPUESTA_TITLE };
    case 'vuelve_a_stock':
      return { label: 'Vuelve a stock (sin dinero)', tone: 'gray', title: nd.note ?? '' };
    case 'no_insistir':
      return { label: 'No insistir', tone: 'gray', title: 'Cerrado a mano.' };
    default:
      return null; // reintegrada / devuelta: ya los muestran los distintivos de siempre
  }
}

export function ndClaimActions(nd: NdClaim | null | undefined): NdClaimAction[] {
  if (!nd) return [];
  const out: NdClaimAction[] = [];
  if (!['reintegrada', 'vuelve_a_stock', 'devuelta', 'no_insistir'].includes(nd.status)) out.push('nd_claim_stop');
  // También en "sin respuesta": el botón deja sin efecto un "No" anterior en Decisiones.
  if (!['reintegrada', 'devuelta'].includes(nd.status)) out.push('nd_claim_retry');
  return out;
}

export const ND_CLAIM_CONFIRM: Record<NdClaimAction, string> = {
  nd_claim_stop: '¿Dejar de reclamar esta venta? No se va a volver a proponer.',
  nd_claim_retry:
    '¿Volver a reclamar esta venta? Se vuelve a proponer en Decisiones en la próxima revisión diaria (9:30), aunque antes hayas dicho que no.',
};
